// global
let alt = false;                    // used to flip class every new month
let monthName = "&nbsp;";             // used to show the month beside the table on every new-month-row
let monthGridStartDate = new Date();  // used to keep track of the left most month in the calendar for navigation
let monthGridMaxNrOfMonths = 12;             // used to limit the number of months to show in the calendar
let monthGridEvents = [];             // used to store the calendar events for the month grid
let monthGridTitle = [];             // used to store the calendar events for the month grid
let monthGridNavPosition = 'top';    // used to store the navigation position for the month grid
let monthGridToken = "";
let monthGridApiUrl = "";

/**
 * TODO: make month names language aware
 */

/**
 * Start building the calendar. This function is called from
 * A backend generated self-invoking function
 * @param token string the token to use for authentication
 * @param apiUrl string where to request data
 * @param display string '*' or one or more of ['month', 'weeklist', 'eventlist', 'nb-eventlist'] - which display type to render (default: '*')
 * @param reqTitle string the title to show above the calendar (default: 'Calendar')
 * @param reqMaxNrOfMonths number the maximum number of months to show in the calendar (default: 12)
 * @param navPosition string the position of the navigation buttons (top, bottom, both, default: 'top')
 */
export async function startCalendar(
  token,
  apiUrl = "http://localhost:8000/api/wpcal",
  display = "*",
  reqTitle = "Calendar",
  reqMaxNrOfMonths = 12,
  navPosition = 'top'
) {
  // display may contain multiple values, so split them on space
  display = display.split(" ");
  // check if we have a target element to render the calendar in
  const target = document.querySelector("#cl_calendar_" + display[0]);
  if (target == null) {
    console.log("error creating calendar, no target elements found");
    return;
  }
  const hasMonthDisplay = display.some((elm) => elm === "month");
  const hasEventListDisplay = display.some((elm) => elm === "*" || elm === "eventlist");
  // these views are bound to the current (or upcoming) school year
  const hasSchoolYearDisplay = display.some((elm) =>
    ["*", "weeklist", "nb-eventlist", "holidays"].includes(elm)
  );
  let data = { calEvents: [], dateExceptionsList: [] };

  if (hasSchoolYearDisplay) {
    // get data for the current (or future if today is in the summer holiday) school year
    data = await getCalendarData(token, apiUrl);
    if (data.message) {
      showCalendarMessage(target, data.message);
      return;
    }
    // /////////////////////////////////////////////////
    // WEEK GRID
    // /////////////////////////////////////////////////
    if (display.some((elm) => elm === "weeklist")) {
      // build calendar grid by week
      const calGrid = buildWeekGrid(data?.calEvents);
      // fill calender grid
      if (calGrid != null) {
        const divTitleCalendar = document.createElement("div");
        divTitleCalendar.innerText = "Kalender vakanties en gebeurtenissen";
        target.appendChild(divTitleCalendar);
        target.appendChild(calGrid);
      }
    }
    // /////////////////////////////////////////////////
    // NON-BLOCKING EVENT LISTS
    // /////////////////////////////////////////////////
    if (display.some((elm) => elm === "nb-eventlist")) {
      // filter out only the non-blocking events
      const nbEvents = data?.dateExceptionsList.filter(ev => ev.plan_blocking === 0);
      // filter out past events
      const todayString = getTodayDateString(false);
      const nbEventsFiltered = nbEvents.filter((ev) => {
        const dtStartAsString = ev.datetime_start.substring(0, 10).replaceAll("-", "");
        return parseInt(dtStartAsString) >= parseInt(todayString);
      });
      const nbEventsFilteredNoHolidays = nbEventsFiltered.filter((ev) => {
        return !ev.is_holiday;
      });
      if (nbEventsFilteredNoHolidays.length === 0) {
        // create an element to show "no events" message
        const div = document.createElement("div");
        div.setAttribute("class", "eu_message");
        div.innerText = "Geen gebeurtenissen gevonden.";
        target.appendChild(div);
      } else {
        target.appendChild(buildEventsList(nbEventsFilteredNoHolidays, reqTitle));
      }
    }
    // /////////////////////////////////////////////////
    // HOLIDAYS
    // /////////////////////////////////////////////////
    if (display.some((elm) => elm === "holidays")) {
      const holidays = data?.dateExceptionsList.filter(ev => ev.is_holiday === 1);
      // filter out past events
      const todayString = getTodayDateString(false);
      const holidaysFiltered = holidays.filter((ev) => {
        const dtStartAsString = ev.datetime_start.substring(0, 10).replaceAll("-", "");
        return parseInt(dtStartAsString) >= parseInt(todayString);
      });
      if (holidaysFiltered.length === 0) {
        const div = document.createElement("div");
        div.setAttribute("class", "eu_message");
        div.innerText = "Geen vakanties gevonden.";
        target.appendChild(div);
      } else {
        target.appendChild(buildHolidayList(holidaysFiltered, reqTitle));
      }
    }

  }

  // /////////////////////////////////////////////////
  // EVENT LISTS
  // /////////////////////////////////////////////////
  if (hasEventListDisplay) {
    // this view has no navigation and shows everything from today onwards,
    // CLASS determines that open-ended window for the eventlist display
    const eventListData = await getCalendarData(token, apiUrl, { display: "eventlist" });
    if (eventListData.message) {
      showCalendarMessage(target, eventListData.message);
      return;
    }
    // remove holiday DE's
    const eventsNoHolidays = (eventListData?.dateExceptionsList ?? []).filter((ev) => {
      return !ev.is_holiday;
    });

    // add a shortlist of date exceptions
    const eventsList = buildEventsList(eventsNoHolidays, reqTitle);
    if (eventsList != null) {
      target.appendChild(eventsList);
    } else {
      showCalendarMessage(target, "Geen vakanties of gebeurtenissen gevonden.");
    }
  }

  // /////////////////////////////////////////////////
  // MONTH GRID
  // /////////////////////////////////////////////////
  if (hasMonthDisplay) {
    monthGridMaxNrOfMonths = parseInt(reqMaxNrOfMonths);
    monthGridTitle = reqTitle;
    monthGridNavPosition = navPosition;
    monthGridToken = token;
    monthGridApiUrl = apiUrl;
    monthGridStartDate = getInitialMonthStart();
    await renderMonthGrid(target);
  }

  // **********************************************************************************
  // Attach event listeners to the calendar nodes
  // **********************************************************************************
  attachEventListeners();
}

/**
 * Show a message (error or "nothing found") in the calendar container
 * @param target HTMLElement the calendar container
 * @param message string the message to show
 */
const showCalendarMessage = (target, message) => {
  const div = document.createElement("div");
  div.setAttribute("class", "eu_message");
  div.innerText = message;
  target.appendChild(div);
};

const getInitialMonthStart = () => {
  const today = new Date();
  return new Date(today.getFullYear(), today.getMonth(), 1);
};

const getMonthWindow = () => {
  const from = new Date(monthGridStartDate.getFullYear(), monthGridStartDate.getMonth(), 1);
  const to = new Date(from.getFullYear(), from.getMonth() + monthGridMaxNrOfMonths, 0);
  return {
    from: formatDate(from),
    to: formatDate(to)
  };
};

const formatDate = (date) => {
  const month = `${date.getMonth() + 1}`.padStart(2, "0");
  const day = `${date.getDate()}`.padStart(2, "0");
  return `${date.getFullYear()}-${month}-${day}`;
};

const renderMonthGrid = async (target) => {
  const dateWindow = getMonthWindow();
  const data = await getCalendarData(monthGridToken, monthGridApiUrl, dateWindow);
  if (data.message) {
    const div = document.createElement("div");
    div.setAttribute("class", "eu_message");
    div.innerText = data.message;
    target.appendChild(div);
    return;
  }
  monthGridEvents = data?.calEvents ?? [];
  const calMonthGrid = buildMonthGrid(monthGridEvents, monthGridTitle, monthGridNavPosition);
  if (calMonthGrid != null) {
    target.innerHTML = "";
    target.appendChild(calMonthGrid);
  }
};

const goPrevHandeler = async () => {
  const target = document.querySelector("#cl_calendar_month");
  monthGridStartDate = new Date(monthGridStartDate.getFullYear(), monthGridStartDate.getMonth() - 1, 1);
  await renderMonthGrid(target);
  attachEventListeners();
};

const goNextHandeler = async () => {
  const target = document.querySelector("#cl_calendar_month");
  monthGridStartDate = new Date(monthGridStartDate.getFullYear(), monthGridStartDate.getMonth() + 1, 1);
  await renderMonthGrid(target);
  attachEventListeners();
};

const goTodayHandeler = async () => {
  const target = document.querySelector("#cl_calendar_month");
  monthGridStartDate = getInitialMonthStart();
  await renderMonthGrid(target);
  attachEventListeners();
};

const attachEventListeners = () => {
  const originatingDomain = window.location.origin;
  const divs = document.querySelectorAll("[data-link]");
  divs.forEach((div) => {
    // If the link is not from the same domain, open it in a new window.
    // Otherwise, open it in the same window
    if (div.dataset.link.indexOf(originatingDomain) === -1) {
      div.addEventListener("click", () => {
        window.open(div.dataset.link, "_blank");
      });
    } else {
      div.addEventListener("click", () => {
        window.location.href = div.dataset.link;
      });
    }
  });
  tippy("[data-tippy-content]", {
    allowHTML: true,
    interactive: true,
    theme: "light-border"
  });
  // navigation for month calendar
  const goPrev = document.querySelector("#goPrev");
  const goNext = document.querySelector("#goNext");
  const goToday = document.querySelector("#goToday");

  if (goPrev != null && goNext != null && goToday != null) {
    goPrev.addEventListener("click", goPrevHandeler);
    goNext.addEventListener("click", goNextHandeler);
    goToday.addEventListener("click", goTodayHandeler);
  }
};

/**
 * Gets the full date string in format 'yyyymmdd' ('mysql notation') for today with padding zero's
 * Purpose: to be able to compare dates as strings being larger or smaller
 * @param keepHyphens boolean if true, return date string with hyphens (default), otherwise without
 * @returns `${number}-${number}-${number}`|`${number}${number}${number}`
 */
const getTodayDateString = (keepHyphens = true) => {
  // special class for 'today'
  const today = new Date();
  let dayNumber = today.getDate();
  if (dayNumber < 10) {
    dayNumber = `0${dayNumber}`;
  }
  let monthNumber = today.getMonth() + 1;
  if (monthNumber < 10) {
    monthNumber = `0${monthNumber}`;
  }
  if (keepHyphens) {
    return `${today.getFullYear()}-${monthNumber}-${dayNumber}`;
  } else {
    return `${today.getFullYear()}${monthNumber}${dayNumber}`;
  }
};

/**
 * Ask CLASS for date exceptions in the current or upcoming school year,
 * or in a specific date window when from/to are provided, or from today
 * onwards when the display is passed.
 * @param token
 * @param apiUrl
 * @param query object with optional from, to and display
 * @returns {Promise<any>}
 */
async function getCalendarData(token, apiUrl, query = null) {
  try {
    const requestUrl = new URL(apiUrl, window.location.origin);
    if (query?.from) {
      requestUrl.searchParams.set("from", query.from);
    }
    if (query?.to) {
      requestUrl.searchParams.set("to", query.to);
    }
    if (query?.display) {
      requestUrl.searchParams.set("display", query.display);
    }
    const data = await fetch(requestUrl.toString(), {
      headers: {
        "Content-Type": "application/json",
        Authorization: `Bearer ${token}`
      },
      credentials: "include"
    });
    return await data.json();
  } catch (error) {
    console.log("error fetching calendar data", error);
    return { message: "Er is iets misgegaan bij het ophalen van de kalender." };
  }
}

/**
 * build calendar grid based on incoming data
 * @param data
 * @param addWeeknumber
 * @returns {HTMLTableElement}
 */
const buildWeekGrid = (data = [], addWeeknumber = true) => {
  const table = document.createElement("table");
  table.setAttribute("class", "table table-date-calendar");
  // create header row
  const headerData = [
    { value: "week" },
    { value: "ma" },
    { value: "di" },
    { value: "wo" },
    { value: "do" },
    { value: "vr" },
    { value: "za" },
    { value: "zo" }
  ];
  const emptyRow = [
    { value: "" },
    { value: "" },
    { value: "" },
    { value: "" },
    { value: "" },
    { value: "" },
    { value: "" },
    { value: "" }
  ];
  table.appendChild(createRow(headerData, "", addWeeknumber ? 8 : 7));
  // iterate data as rows (TR), add week number column if requested
  data.forEach((week, idx) => {
    let rowData;
    if (addWeeknumber) {
      rowData = [
        {
          value: week.weeknumber,
          className:
            parseInt(week.weeknumber) % 2 === 0
              ? "week-number even"
              : "week-number"
        },
        ...week.data
      ];
    } else {
      rowData = week.data;
    }
    table.appendChild(createRow(rowData, "", addWeeknumber ? 9 : 8));
    if (idx % 2 !== 0) {
      table.appendChild(
        createRow(emptyRow, "separator", addWeeknumber ? 9 : 8)
      );
    }
  });
  return table;
};

/**
 * Build a calendar grid based on incoming data
 * @param data array of objects: {weeknumber, data: [{date, className, mouseover}]}
 * @param title string the title to show above the calendar (default: 'Calendar')
 * @param navPosition string the position of the navigation buttons (top, bottom, both, default: 'top')
 * @returns {HTMLTableElement}
 */
const buildMonthGrid = (data = [], title = "Calendar", navPosition = 'top') => {

  // this is the container for the calendar
  const table = document.createElement("div");
  table.setAttribute("class", "table table-date-calendar");

  // add the table title
  const titleRow = document.createElement("div");
  titleRow.setAttribute("class", "table-title");

  const captionBlock = document.createElement("div");
  captionBlock.setAttribute("class", "table-caption");
  const titleElement = document.createElement("h3");
  titleElement.innerHTML = title;
  captionBlock.appendChild(titleElement);

  titleRow.appendChild(captionBlock);
  // if monthGridMaxNrOfMonths is < 12 we need navigation: prev and next month
  // make the scope available for a footer section
  const navBlock = document.createElement("div");
  if (monthGridMaxNrOfMonths < 12) {
    navBlock.setAttribute("class", "table-nav");
    navBlock.innerHTML = `<div class="table-nav">` +
      `<span id="goToday" class="table-nav-button table-nav-button-today">Vandaag</span>` +
      `<span id="goPrev" class="table-nav-button table-nav-button-prev">&lt; Vorige</span>` +
      `<span id="goNext" class="table-nav-button table-nav-button-next">Volgende &gt;</span>` +
      `</div>`;
    if (navPosition === 'both' || navPosition === 'top') titleRow.appendChild(navBlock);
  }
  table.appendChild(titleRow);

  // use the requested visible window, not the first returned event row.
  let firstMonth = monthGridStartDate.getMonth() + 1;
  let firstYear = monthGridStartDate.getFullYear();

  let calRows = [];
  let monthToAppend = firstMonth;
  let yearToAppend = firstYear;
  // index = month number, hence start at 1 and a stub entry at 0
  const monthNames = [
    { shortname: "stub", longname: "stub" },
    { shortname: "jan", longname: "januari" }, { shortname: "feb", longname: "februari" },
    { shortname: "mrt", longname: "maart" }, { shortname: "apr", longname: "april" },
    { shortname: "mei", longname: "mei" }, { shortname: "jun", longname: "juni" },
    { shortname: "jul", longname: "juli" }, { shortname: "aug", longname: "augustus" },
    { shortname: "sep", longname: "september" }, { shortname: "okt", longname: "oktober" },
    { shortname: "nov", longname: "november" }, { shortname: "dec", longname: "december" }
  ];

  for (let i = 0; i < monthGridMaxNrOfMonths; i++) {
    // If we start the first month at 1 (januari), we will never pass the year boundary,
    // because we start at the first month and iterate monthGridMaxNrOfMonths times.
    // Otherwise, we cross the year boundary when we reach december
    if (firstMonth > 1 && monthToAppend === 13) {
      monthToAppend = 1;
      yearToAppend++;
    }
    calRows.push({ nr: monthToAppend, year: yearToAppend, ...monthNames[monthToAppend] });
    monthToAppend++;
  }

  // Split the result into max 3 rows of 4 months each
  const rows = calRows.reduce((resultArray, item, index) => {
    const chunkIndex = Math.floor(index / monthGridMaxNrOfMonths);
    if (!resultArray[chunkIndex]) {
      resultArray[chunkIndex] = []; // start a new row
    }
    resultArray[chunkIndex].push(item);
    return resultArray;
  }, []);

  // the row order is always the same, so we can "hardcode" it.
  // however, monthGridMaxNrOfMonths may change, so we may not need all rows.
  // monthGridMaxNrOfMonths dictates the length of the rows array
  for (let rowIdx = 0; rowIdx < rows.length; rowIdx++) {
    // every row is a DIV
    const newCell = document.createElement("div");
    newCell.setAttribute("class", "month-row");
    // every row has 4 months
    rows[rowIdx].forEach((month) => {
      // of which every month is a table as a child of the parent TD cell (newCell)
      // filter data to only contain the relevant month's data
      // safari fails if the month number is not 2-digit
      const monthNr = month.nr < 10 ? `0${month.nr}` : month.nr + "";
      // filter on weeks that contain the relevant month.
      // sometimes, the first days of January are still in the last week of the previous year
      // or the last days of December are in the first week of the next year
      const dataForMonth = data.filter((week) => {
        return week.data.some(day => day.date.startsWith(`${month.year}-${monthNr}`));
      });
      // get the month calendar (as a table) and append it to the parent cell
      createCalendarMonthGrid(month, month.year, newCell, dataForMonth);
    });
    table.appendChild(newCell);
  }

  if (navPosition === "both" || navPosition === "bottom") {
    const bottomRow = document.createElement("div");
    bottomRow.setAttribute("class", "table-bottom");
    // we can reuse the navBlock here, make a clone
    const navBlockBottom = navBlock.cloneNode(true);
    bottomRow.appendChild(navBlockBottom);
    table.appendChild(bottomRow);
  }

  return table;
};

/**
 * Create a grid for a (one) month of a year
 * @param month
 * @param year
 * @param parentCell TD that will contain the month grid
 * @param data events for this month
 * @returns {HTMLTableElement}
 */
const createCalendarMonthGrid = (month, year, parentCell, data = []) => {
  const jsMonth = month.nr - 1;
  // how many days are in this month?
  const daysInMonth = new Date(year, month.nr, 0).getDate();
  // what day of the week is the first day of the month?
  // Sunday - Saturday: 0-6
  const firstDayOfMonth = new Date(year, jsMonth, 1).getDay();
  const emptyCellsToStartWith = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;
  // new table for this month
  const table = document.createElement("table");
  table.setAttribute("class", "table table-month");
  // create header row
  const headerRow = document.createElement("tr");
  headerRow.setAttribute("class", "header");
  const headerCell = document.createElement("th");
  headerCell.setAttribute("colspan", "7");
  headerCell.innerHTML = `${month.shortname} - ${year}`;
  headerRow.appendChild(headerCell);
  // create day names row
  const dayNamesRow = document.createElement("tr");
  dayNamesRow.setAttribute("class", "daynames");
  // todo: make day names language independent
  const dayNames = ["ma", "di", "wo", "do", "vr", "za", "zo"];
  dayNames.forEach((dayname) => {
    const dayNameCell = document.createElement("td");
    dayNameCell.innerHTML = dayname;
    dayNamesRow.appendChild(dayNameCell);
  });

  // *************************************************************
  // *********** EMPTY CELLS TO START WITH ***********************
  // *************************************************************
  // Monday is the first day in this grid, so a Monday start needs no leading cells.
  let totalNrOfCreatedRows = emptyCellsToStartWith > 0 ? 1 : 0;
  const emptyCellsToStartWithRow = document.createElement("tr");
  emptyCellsToStartWithRow.setAttribute("class", "empty");
  for (let i = 0; i < emptyCellsToStartWith; i++) {
    createDataCell("&nbsp;", emptyCellsToStartWithRow, "td");
  }

  const dataObject = {};
  data.forEach((week) => {
    week.data.forEach((day) => {
      dataObject[day.date.replaceAll("-", "")] = day;
    });
  });

  // *************************************************************
  // *********** DAYS IN THE OTHERWISE EMPTY FIRST ROW ***********
  // *************************************************************
  // append days to the emptyCellsToStartWithRow starting with day 1
  // keep track of the last day rendered, so we can start with the next day on the next row
  let lastDayRendered = -1;
  if (emptyCellsToStartWith > 0) {
    for (let i = 1; i <= 7 - emptyCellsToStartWith; i++) {
      const dayForDataAttr = i < 10 ? `0${i}` : `${i}`;
      const monthForDataAttr = month.nr < 10 ? `0${month.nr}` : `${month.nr}`;
      const dataForDay = dataObject[`${year}${monthForDataAttr}${dayForDataAttr}`] ?? { date: `${year}-${monthForDataAttr}-${dayForDataAttr}` };
      const styling = getStyling(dataForDay);
      createDataCell(
        `<span class="date">${i}</span>` + getEventCubes(styling.extraStyling),
        emptyCellsToStartWithRow,
        "td",
        7,
        styling.className,
        styling.mouseOver,
        `${year}${monthForDataAttr}${dayForDataAttr}`,
        styling.extraAttributes
      );
      lastDayRendered = i;
    }
  }
  table.appendChild(headerRow);
  table.appendChild(dayNamesRow);
  if (emptyCellsToStartWith > 0) {
    table.appendChild(emptyCellsToStartWithRow);
  }

  // *************************************************************
  // *********** DAYS IN FULLY FILLED ROWS ***********************
  // ******** (except at the end of the month) *******************
  // *************************************************************
  // -1 indicates that there were no days in the otherwise empty first row (so day number 1 is monday)
  let dayCounter = lastDayRendered === -1 ? 1 : lastDayRendered + 1;
  // create rows for the days in pairs of 7 (is a TR) and start with the day after the last day of the previous row
  let dayCounterEnd = dayCounter + 6;
  while (dayCounter <= daysInMonth) {
    totalNrOfCreatedRows++;
    const dayRow = document.createElement("tr");
    dayRow.setAttribute("class", "days");
    for (let i = dayCounter; i <= dayCounterEnd; i++) {
      if (i <= daysInMonth) {
        const dayForDataAttr = i < 10 ? `0${i}` : i;
        const monthForDataAttr = month.nr < 10 ? `0${month.nr}` : month.nr;
        const dataForDay = dataObject[`${year}${monthForDataAttr}${dayForDataAttr}`] ?? { date: `${year}-${monthForDataAttr}-${dayForDataAttr}` };
        const styling = getStyling(dataForDay);
        createDataCell(
          `<span class="date">${i}</span>` + getEventCubes(styling.extraStyling),
          dayRow,
          "td",
          7,
          styling.className,
          styling.mouseOver,
          `${year}${monthForDataAttr}${dayForDataAttr}`,
          styling.extraAttributes
        );
      } else {
        // empty cells to end with, complementing the row
        createDataCell("&nbsp;", dayRow, "td");
      }
    }
    table.appendChild(dayRow);
    dayCounter = dayCounterEnd + 1;
    dayCounterEnd = dayCounter + 6;
  }
  parentCell.appendChild(table);
};

/**
 * Returns the styling for a day cell based on the mouseover attribute and the className attribute in the data.
 *
 * @param dataForDay the data from the API call filtered for this day
 * @returns {{className: string, extraAttributes: {}, mouseOver, extraStyling}}
 */
const getStyling = (dataForDay) => {
  //debugger;
  const linkIcon = "&#x1F517;"; // = 🔗
  const currentDate = getTodayDateString();
  // today get extra class
  const isToday = dataForDay?.date === currentDate;
  let mouseOver = dataForDay?.mouseover ?? "";
  let extraClass = dataForDay?.className ?? "";
  // The mouseover attribute contains e.g. 'Grand Opening Day|#34DDE5|http://www.mysite.com/grand-opening'
  // this is the case for non-blocking date exceptions.
  // For blocking date exceptions, the mouseover attribute should only contain the reason, not a color
  // multiple events on 1 day are separated by ":~:" (I know...)
  let extraStyling = [];
  const eventParts = dataForDay?.mouseover?.split(":~:") ?? [];
  let titleDateNL = dataForDay?.date.split("-").reverse().join("-");
  // If it contains more than date + 'open' or 'closed', it contains events
  // The most complex case is when we have a blocking event "holiday" -and- a non-blocking event "grand opening" on
  // the same day. In that case, we also have an "eventParts[1]" with the non-blocking event (recognizable by the "|"
  // separator). The blocking event is always first, so we can safely assume that the first event is blocking.
  if (
    eventParts.length > 0 &&
    (eventParts[0].includes("|") ||
      (eventParts[1] && eventParts[1].includes("|")))
  ) {
    mouseOver = titleDateNL + "<hr>";
    eventParts.forEach((event) => {
      const parts = event.split("|");
      if (parts.length > 1) {
        const color = parts[1] ?? "";
        const link = parts[2] ?? "";
        const startTime = parts[3] ?? "";
        const endTime = parts[4] ?? "";
        const timeSegment =
          startTime !== "" && endTime !== ""
            ? `&nbsp;${startTime}&nbsp;-&nbsp;${endTime}`
            : "";
        extraStyling.push({ eventColor: color, link: link });
        let mouseOverText = parts[0].trim();
        // if we have a link, add the link icon
        if (link !== "") {
          mouseOverText = `<a href="${link}">${mouseOverText}&nbsp;${linkIcon}</a>`;
        }
        mouseOver += `<span>${mouseOverText}${timeSegment}</span><br>`;
      } else {
        mouseOver += `<span>${event}</span><br>`;
      }
      // this overrules the extra class, which will show 'closed',
      // but this is a non-blocking exception, so it should be open
      // however, if the first event is a blocking event, the extra class will be 'closed'
      extraClass = eventParts[0].includes("|") ?
        "open" :
        extraClass.includes("alt") ? extraClass + " closed" : "closed";
    });
  } else {
    let hoverText = eventParts[0];
    // only if it's a Sunday, add the weekend-closed class
    if (
      dataForDay?.date !== "" &&
      new Date(dataForDay?.date).getDay() === 0 &&
      hoverText != null
    ) {
      // remove the word 'Open' from the hoverText,
      // this is data from backend that needs to be more aware of the school's opening hours
      hoverText = hoverText.replace("Open", "");
      extraClass.includes("alt") ? extraClass : "weekend-closed";
      mouseOver = `<span>${hoverText}</span>`;
    } else {
      mouseOver = `${titleDateNL}<br><span>${hoverText}</span>`;
    }
  }

  let extraAttributes = {}; // @deprecated variable will be deprecated because it can only hold one attribute
  let className = `day-cell ${extraClass}`;
  if (isToday) {
    className += " today";
    mouseOver += " (vandaag)";
  }
  if (extraClass.includes("alt")) {
    mouseOver += "<br>(alternatief programma)";
  } else if (extraClass.includes("closed")) {
    mouseOver += "<br>(geen lessen)";
  }
  return { className, extraAttributes, mouseOver, extraStyling };
};

/**
 * Get the event cubes for a specific day
 * @param cubeData array of objects: { eventColor, mouseOver }
 * @returns {string}
 */
const getEventCubes = (cubeData) => {
  let content = "<div class='events'>";
  if (cubeData.length > 0) {
    cubeData.forEach(event => {
      const evBgColor = event.eventColor ?? "";
      content += `<span class="square" style="background-color:${evBgColor};">&nbsp;</span>`;
    });
  } else {
    // make sure it takes the same amount of space as the event cubes
    content += "<span>&nbsp;</span>";
  }
  content += "</div>";
  return content;
};

/**
 * Get the contrasting color (black or white) for a specific hex color
 * defaults to black
 * @param hexcolor
 * @returns {string}
 */
const getContrastingColor = (hexcolor = "") => {
  if (hexcolor === "") return "black";
  // If a leading # is provided, remove it
  if (hexcolor.slice(0, 1) === "#") {
    hexcolor = hexcolor.slice(1);
  }

  // Convert to RGB value
  const r = parseInt(hexcolor.substring(0, 2), 16);
  const g = parseInt(hexcolor.substring(2, 4), 16);
  const b = parseInt(hexcolor.substring(4, 6), 16);

  // Get YIQ ratio
  const yiq = (r * 299 + g * 587 + b * 114) / 1000;

  // Check contrast
  return yiq >= 128 ? "black" : "white";
};

/**
 * Get the week number for a specific date.
 * @see https://stackoverflow.com/questions/6117814/get-week-of-year-in-javascript-like-in-php
 * but take care that sunday is the last day of the week, not the first day of the next week!
 * @param dateString
 * @returns {number}
 */
const getWeekNumber = (dateString) => {
  const date = new Date(dateString);
  date.setHours(0, 0, 0, 0);
  // Thursday in the current week decides the year.
  date.setDate(date.getDate() + 3 - ((date.getDay() + 6) % 7));
  // January 4 is always in week 1.
  const week1 = new Date(date.getFullYear(), 0, 4);
  // Adjust to Thursday in week 1 and count the number of weeks from date to week1.
  return (
    1 + Math.round(((date.getTime() - week1.getTime()) / 86400000 - 3 + ((week1.getDay() + 6) % 7)) / 7)
  );
};

/**
 * Get the number of days in a specific month of a specific year
 * @param year
 * @param month
 * @returns {number}
 */
const getDaysInMonth = (year, month) => {
  return new Date(year, month, 0).getDate();
};

/**
 * Create data cell (TD/TH) containing the given value
 * @param content (value inside the TD/TH)
 * @param trElement element to append the TD/TH onto
 * @param className
 * @param setCommonWidthToNrOfCols number of columns, used to set all cells equal width
 * @param cellType one of {th, td}
 * @param hoverText to be displayed on hover
 * @param dataDate date in format YYYYMMDD - will be added to the cell as data-date attribute
 * @param extraAttributes object with extra attributes to be added to the cell
 */
function createDataCell(
  content = {},
  trElement,
  cellType = "td",
  setCommonWidthToNrOfCols = 7,
  className = "",
  hoverText = "",
  dataDate = "",      // will be added to the cell as data-date attribute (should be the date in format YYYYMMDD)
  extraAttributes = {}
) {
  const cellElement = document.createElement(cellType);
  if (className !== "") {
    cellElement.setAttribute("class", className);
  }
  if (hoverText != null && hoverText !== "" && !hoverText.includes("undefined")) {
    cellElement.setAttribute("data-tippy-content", hoverText);
  }
  if (dataDate !== "") {
    cellElement.setAttribute("data-date", dataDate);
  }
  if (extraAttributes) {
    for (const [key, value] of Object.entries(extraAttributes)) {
      cellElement.setAttribute(key, value);
    }
  }
  cellElement.innerHTML = content;
  trElement.appendChild(cellElement);
}

/**
 * Create 1 TR with cells
 * @param dataForRow array of object: {value, className}
 * @param rowType one of {'', 'header', 'separator'} (creates th instead of td or empty row)
 * @param nrOfColumns
 * @param showMonthLable
 * @param styleForTR style attribute for the TR or ""
 * @returns {HTMLTableRowElement}
 */
function createRow(
  dataForRow = [],
  rowType = "",
  nrOfColumns = 8,
  showMonthLable = true,
  styleForTR = ""
) {
  const tr = document.createElement("tr");
  if (rowType === "separator") {
    tr.setAttribute("class", "separator");
  }

  const optionsMonthName = { month: "long" };
  const cellType = rowType === "header" ? "th" : "td";

  dataForRow.forEach((data) => {
    const celldate = new Date(data.date);
    let className = rowType === "header" ? "header" : data?.className || "";
    if (data.value === "01") {
      // flip alt on every new month
      alt = !alt;
      monthName = celldate.toLocaleDateString("nl-NL", optionsMonthName);
    }
    className = alt && className !== "" ? className + " alt" : className;
    const extraAttributes = styleForTR !== "" ? { style: styleForTR } : {};
    createDataCell(
      data.value,
      tr,
      cellType,
      nrOfColumns,
      className,
      data.mouseover,
      "",
      extraAttributes
    );
  });
  // shows month name (if this row has a 01 value, see before) or empty cell
  if (showMonthLable) {
    createDataCell(monthName, tr, cellType, nrOfColumns, "monthlabel", "");
    monthName = "&nbsp;";
  }
  return tr;
}

/**
 * Create a table with all events, blocking and non-blocking
 * Also indicate color in calendar as a legend
 * @param data
 * @param title
 * @returns {HTMLTableElement}
 */
function buildEventsList(data = [], title = "Lijst vakanties en gebeurtenissen") {

  // Create a wrapper for title plus event-list
  const wrapper = document.createElement("div");
  wrapper.setAttribute("class", "wrapper");

  // Create the container for the list of events (flexbox)
  const parentDiv = document.createElement("div");
  parentDiv.setAttribute("class", "list-events");

  // Create the title section to be put above the event-list container
  const divTitleDateList = document.createElement("div");
  // wrap title in H2
  const titleElement = document.createElement("h3");
  titleElement.innerText = title;
  divTitleDateList.appendChild(titleElement);
  wrapper.appendChild(divTitleDateList);

  groupEventsByBlockingException(data).forEach((entry, index) => {
    if (entry.items.length === 0) {
      parentDiv.appendChild(createEventTile(entry.dateItem, index));
      return;
    }
    // A blocking whole-day date exception (e.g. a workshop week) wraps the
    // non-blocking items (published events, date exceptions) inside its range
    const groupDiv = document.createElement("div");
    groupDiv.setAttribute("class", "event-group");
    groupDiv.appendChild(createEventTitle(entry.dateItem, "event-group-title"));
    const itemsDiv = document.createElement("div");
    itemsDiv.setAttribute("class", "list-events event-group-items");
    entry.items.forEach((dateItem, itemIndex) => {
      itemsDiv.appendChild(createEventTile(dateItem, itemIndex));
    });
    groupDiv.appendChild(itemsDiv);
    parentDiv.appendChild(groupDiv);
  });
  wrapper.appendChild(parentDiv);
  return wrapper;
}

/**
 * Whole-day items start at 00:00:00 and end at 23:59:59 (CLASS convention)
 * @param dateItem
 * @returns {boolean}
 */
const isWholeDayItem = (dateItem) => {
  return dateItem.datetime_start.split(" ")[1] === "00:00:00" &&
    dateItem.datetime_end.split(" ")[1] === "23:59:59";
};

/**
 * A blocking, whole-day date exception (workshop week, festive week) acts as a
 * container for the non-blocking items that fall within its range
 * @param dateItem
 * @returns {boolean}
 */
const isGroupingException = (dateItem) => {
  return parseInt(dateItem.plan_blocking) === 1 && isWholeDayItem(dateItem);
};

/**
 * Group the (date sorted) items of the event list: every non-blocking item that lies
 * completely within a blocking whole-day date exception is moved into that exception.
 * Datetime strings are in 'YYYY-MM-DD HH:MM:SS' notation, so they compare as strings.
 * @param data array of date exceptions and published events, sorted by datetime_start
 * @returns {Array<{dateItem, items: Array}>} top level entries in original order
 */
const groupEventsByBlockingException = (data = []) => {
  const entries = [];
  const groups = data
    .filter(isGroupingException)
    .map((dateItem) => ({ dateItem, items: [] }));
  data.forEach((dateItem) => {
    if (isGroupingException(dateItem)) {
      entries.push(groups.find((group) => group.dateItem === dateItem));
      return;
    }
    const container = parseInt(dateItem.plan_blocking) === 1 ?
      undefined :
      groups.find((group) =>
        dateItem.datetime_start >= group.dateItem.datetime_start &&
        dateItem.datetime_end <= group.dateItem.datetime_end
      );
    if (container) {
      container.items.push(dateItem);
    } else {
      entries.push({ dateItem, items: [] });
    }
  });
  return entries;
};

/**
 * Format the date (range) or date + start time of an item in the event list
 * @param dateItem
 * @returns {string}
 */
const formatEventRange = (dateItem) => {
  const startDateObj = new Date(dateItem.datetime_start.replace(" ", "T"));
  const endDateObj = new Date(dateItem.datetime_end.replace(" ", "T"));

  const day = String(startDateObj.getDate()).padStart(2, "0");
  const month = String(startDateObj.getMonth() + 1).padStart(2, "0");
  const year = startDateObj.getFullYear();
  const endDay = String(endDateObj.getDate()).padStart(2, "0");
  const endMonth = String(endDateObj.getMonth() + 1).padStart(2, "0");
  const endYear = endDateObj.getFullYear();

  if (isWholeDayItem(dateItem)) {
    // All-day or multi-day event, show only dates
    const formattedStartDate = `${day}-${month}-${year}`;
    const formattedEndDate = `${endDay}-${endMonth}-${endYear}`;
    return formattedStartDate === formattedEndDate ?
      formattedStartDate :
      `${formattedStartDate} t/m ${formattedEndDate}`;
  }
  // Show date and time for start
  const hours = String(startDateObj.getHours()).padStart(2, "0");
  const minutes = String(startDateObj.getMinutes()).padStart(2, "0");
  return `${day}-${month}-${year} ${hours}:${minutes}`;
};

/**
 * Create the title block (coloured label plus date/timeline) of an event list item
 * @param dateItem
 * @param className class for the block, the label itself always gets 'event-title'
 * @returns {HTMLDivElement}
 */
const createEventTitle = (dateItem, className = "event-title") => {
  // e.g. "Kerstvakantie", "#ea439c", "https://www.google.com"
  // for studentgroup: "workshop groep 1", "for 10 - 15 year", "#34dde5", "https://www.google.com"
  const labelFirstPart = dateItem?.student?.lastname ?? dateItem.reason;
  const labelSecondPart = dateItem?.subtitle ?? "";
  const label = labelFirstPart + (labelSecondPart !== "" ? "<br><small>" + labelSecondPart + "</small>" : "");

  const backgroundColor = dateItem?.calendar_color ?? "";
  let extraAttributes = backgroundColor !== "" ? { style: "background-color: " + backgroundColor } : {};
  const link = dateItem?.detail_url ?? "";
  if (link !== "") {
    extraAttributes = { ...extraAttributes, "data-link": link };
  }
  const hoverText = dateItem.mouseOver ?? "";
  const titleDiv = document.createElement("div");
  titleDiv.setAttribute("class", className);
  // title cell. &#x1F517; = link icon
  createDataCell(
    label + (link !== "" ? "&nbsp;&#x1F517;" : ""),
    titleDiv,
    "div",
    3,
    "event-title",
    hoverText,
    "",
    extraAttributes
  );
  // content cell, now: formatted date and/or time
  createDataCell(formatEventRange(dateItem), titleDiv, "div", 3, "event-description", hoverText);
  return titleDiv;
};

/**
 * Create one tile of the event list
 * @param dateItem
 * @param index position in its list, determines the tile class
 * @returns {HTMLDivElement}
 */
const createEventTile = (dateItem, index = 0) => {
  const eventDivClasses = ["event-left", "event-right", "event-middle"];
  const eventDiv = document.createElement("div");
  eventDiv.setAttribute("class", eventDivClasses[index % 3]);
  eventDiv.appendChild(createEventTitle(dateItem));
  return eventDiv;
};

const buildHolidayList = (data = [], title = "Lijst vakanties en andere sluitingsdagen") => {
  const wrapper = document.createElement("div");
  wrapper.setAttribute("class", "wrapper");

  // Create the title section to be put above the event-list container
  const divTitleDateList = document.createElement("div");
  // wrap title in H2
  const titleElement = document.createElement("h3");
  titleElement.innerText = title;
  divTitleDateList.appendChild(titleElement);
  wrapper.appendChild(divTitleDateList);

  // Datablock with holidays
  const table = document.createElement("table");
  table.setAttribute("class", "table table-holiday-list");
  // const headerData = [{ value: "datum" }, { value: "naam" }];
  // table.appendChild(createRow(headerData, "header", 2, false));
  data.forEach((holiday) => {
    // show date range as string: day month name year
    const options = {
      year: "numeric",
      month: "long",
      day: "numeric"
    };
    const dateFrom = new Date(holiday.datetime_start.substring(0, 10)).toLocaleDateString("nl-NL", options);
    const dateTo = new Date(holiday.datetime_end.substring(0, 10)).toLocaleDateString("nl-NL", options);
    const dateRange = `${dateFrom} t/m ${dateTo}`;
    const rowData = [{ value: dateRange }, { value: holiday.reason }];
    table.appendChild(createRow(rowData, "", 2, false));
  });
  wrapper.appendChild(table);
  return wrapper;
};

export const startOpeningTimes = async (
  token,
  apiUrl = "http://localhost:8000/api/wpopeningtimes"
) => {
  const target = document.querySelector("#cl_openingtimes");
  if (target == null) {
    console.log("error showing opening times, no target elements found");
    return;
  }
  const data = await getOpeningTimes(token, apiUrl);
  const div = document.createElement("div");
  const strong = document.createElement("strong");
  strong.innerText = "Openingstijden";
  div.appendChild(strong);
  div.appendChild(document.createElement("br"));
  div.appendChild(document.createElement("hr"));
  const daysOfWeek = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday"
  ];
  // todo: create multilang version
  const dayNamesNL = {
    monday: "maandag",
    tuesday: "dinsdag",
    wednesday: "woensdag",
    thursday: "donderdag",
    friday: "vrijdag",
    saturday: "zaterdag",
    sunday: "zondag"
  };
  daysOfWeek.forEach((day) => {
    const dayData = data[day];
    let line = dayNamesNL[day] + ": ";
    if (dayData === null) {
      line += "Gesloten";
    } else {
      const openTime = dayData.open.substring(0, 5); // HH:MM
      const closeTime = dayData.close.substring(0, 5); // HH:MM
      line += `${openTime} - ${closeTime}`;
    }
    div.appendChild(document.createTextNode(line));
    div.appendChild(document.createElement("br"));
  });
  target.appendChild(div);
};

const getOpeningTimes = async (token, apiUrl) => {
  const response = await fetch(apiUrl, {
    method: "GET",
    headers: {
      Authorization: `Bearer ${token}`
    }
  });
  return response.json();
};

